import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { FileDown, TrendingUp, TrendingDown, Users, CalendarDays, IndianRupee, Building2, Stethoscope } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend, ComposedChart, Area
} from 'recharts';

const COLORS = ['#0D8B7D', '#F5B041', '#4A6B68', '#D3544E', '#88D4CC', '#FAD7A1'];
const REPORT_TYPES = [
  { value: 'branch-wise', label: 'Branch Wise Summary' },
  { value: 'month-wise', label: 'Month Wise Summary' },
  { value: 'new-clients', label: 'New Clients Summary' },
  { value: 'footfall', label: 'Footfall Summary' },
  { value: 'revenue', label: 'Revenue Summary' },
  { value: 'overall', label: 'Overall Summary' },
];

function KpiCard({ label, value, icon: Icon, prefix, suffix, trend }) {
  return (
    <Card className="bg-white border-[#E5F2F1] rounded-2xl shadow-[0_2px_12px_rgba(13,139,125,0.04)]">
      <CardContent className="p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#0D8B7D]/8 flex items-center justify-center">
          <Icon className="w-4 h-4 text-[#0D8B7D]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xl font-semibold text-[#1A3330] font-['Outfit'] truncate">
            {prefix}{typeof value === 'number' ? value.toLocaleString('en-IN') : value}{suffix}
          </p>
          <p className="text-[11px] text-[#4A6B68]">{label}</p>
        </div>
        {trend !== undefined && trend !== null && (
          <div className={`flex items-center gap-0.5 text-xs font-medium ${trend >= 0 ? 'text-[#2E8B57]' : 'text-[#D3544E]'}`}>
            {trend >= 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
            {Math.abs(trend)}%
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DataTable({ columns, rows = [], testId }) {
  return (
    <Card className="bg-white border-[#E5F2F1] rounded-2xl overflow-hidden" data-testid={testId}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-[#F4FAF9] border-b border-[#E5F2F1]">
            {columns.map(c => <th key={c.key} className="text-left px-4 py-3 text-[11px] font-semibold text-[#4A6B68] uppercase tracking-wider">{c.label}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className="border-b border-[#E5F2F1] last:border-0 hover:bg-[#F4FAF9]/50 transition-colors">
                {columns.map(c => <td key={c.key} className="px-4 py-3 text-[#1A3330]">{c.format ? c.format(row[c.key]) : row[c.key]}</td>)}
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={columns.length} className="px-4 py-6 text-center text-[#4A6B68]">No data available</td></tr>}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function ChartCard({ title, children, testId }) {
  return (
    <Card className="bg-white border-[#E5F2F1] rounded-2xl" data-testid={testId}>
      <CardContent className="p-5">
        <h3 className="text-sm font-semibold text-[#1A3330] font-['Outfit'] mb-4">{title}</h3>
        {children}
      </CardContent>
    </Card>
  );
}

const fmtINR = (v) => `Rs.${(v || 0).toLocaleString('en-IN')}`;
const fmtMonth = (m) => { if (!m) return ''; const [y, mo] = m.split('-'); const d = new Date(y, mo - 1); return d.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' }); };

export default function ReportsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const [reportType, setReportType] = useState('overall');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [branchId, setBranchId] = useState('');
  const [branches, setBranches] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [branchLocked, setBranchLocked] = useState(false);

  useEffect(() => {
    api.get('/reports/my-branches').then(r => {
      const b = r.data.branches || [];
      setBranches(b);
      setBranchLocked(r.data.locked);
      if (r.data.locked && b.length === 1) setBranchId(b[0]._id);
      else if (!isAdmin && b.length > 0) setBranchId('all');
    }).catch(() => {});
    const now = new Date();
    const from = new Date(now.getFullYear(), now.getMonth() - 5, 1);
    setDateFrom(from.toISOString().split('T')[0]);
    setDateTo(now.toISOString().split('T')[0]);
  }, [isAdmin]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      if (branchId && branchId !== 'all') params.set('branch_id', branchId);
      const { data: res } = await api.get(`/reports/${reportType}?${params}`);
      setData(res);
    } catch {} finally { setLoading(false); }
  }, [reportType, dateFrom, dateTo, branchId]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const exportPDF = () => { window.print(); };

  const renderReport = () => {
    if (!data) return null;
    switch (reportType) {
      case 'branch-wise': return <BranchWiseReport data={data} />;
      case 'month-wise': return <MonthWiseReport data={data} />;
      case 'new-clients': return <NewClientsReport data={data} />;
      case 'footfall': return <FootfallReport data={data} />;
      case 'revenue': return <RevenueReport data={data} />;
      case 'overall': return <OverallReport data={data} isAdmin={isAdmin} />;
      default: return null;
    }
  };

  return (
    <div className="space-y-5" data-testid="reports-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-semibold text-[#1A3330] font-['Outfit']">Reports</h1>
        {isAdmin && (
          <Button onClick={exportPDF} variant="outline" className="rounded-xl border-[#E5F2F1] text-[#0D8B7D] text-xs" data-testid="export-pdf-btn">
            <FileDown className="w-3.5 h-3.5 mr-1.5" />Export to PDF
          </Button>
        )}
      </div>

      {/* Report Selector & Filters */}
      <Card className="bg-white border-[#E5F2F1] rounded-2xl">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="min-w-[220px]">
              <Label className="text-[11px] text-[#4A6B68] uppercase tracking-wider">Select Report</Label>
              <Select value={reportType} onValueChange={setReportType}>
                <SelectTrigger className="rounded-xl border-[#E5F2F1] mt-1 h-9" data-testid="report-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{REPORT_TYPES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] text-[#4A6B68] uppercase tracking-wider">From</Label>
              <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="rounded-xl border-[#E5F2F1] mt-1 h-9 text-sm w-[150px]" data-testid="date-from" />
            </div>
            <div>
              <Label className="text-[11px] text-[#4A6B68] uppercase tracking-wider">To</Label>
              <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="rounded-xl border-[#E5F2F1] mt-1 h-9 text-sm w-[150px]" data-testid="date-to" />
            </div>
            <div className="min-w-[180px]">
              <Label className="text-[11px] text-[#4A6B68] uppercase tracking-wider">Branch</Label>
              {branchLocked && branches.length === 1 ? (
                <div className="mt-1 h-9 px-3 flex items-center rounded-xl border border-[#E5F2F1] bg-[#F4FAF9] text-sm text-[#1A3330]">{branches[0].name}</div>
              ) : (
                <Select value={branchId} onValueChange={setBranchId}>
                  <SelectTrigger className="rounded-xl border-[#E5F2F1] mt-1 h-9" data-testid="branch-filter"><SelectValue placeholder={isAdmin ? "All Branches" : "All My Branches"} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{isAdmin ? 'All Branches' : 'All My Branches'}</SelectItem>
                    {branches.map(b => <SelectItem key={b._id} value={b._id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-[#0D8B7D] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : renderReport()}
    </div>
  );
}

function BranchWiseReport({ data }) {
  const { rows = [], kpis = {} } = data || {};
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KpiCard label="Total Clients" value={kpis.total_clients} icon={Users} />
        <KpiCard label="Total Sessions" value={kpis.total_sessions} icon={CalendarDays} />
        <KpiCard label="Total Revenue" value={kpis.total_revenue} icon={IndianRupee} prefix="Rs." />
      </div>
      <DataTable testId="branch-table" columns={[
        { key: 'branch_name', label: 'Branch' },
        { key: 'total_clients', label: 'Total Clients' },
        { key: 'active_clients', label: 'Active Clients' },
        { key: 'total_sessions', label: 'Total Sessions' },
        { key: 'completed', label: 'Completed' },
        { key: 'cancelled', label: 'Cancelled' },
        { key: 'revenue', label: 'Revenue', format: fmtINR },
      ]} rows={rows} />
      <ChartCard title="Branch Comparison" testId="branch-chart">
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={rows}><CartesianGrid strokeDasharray="3 3" stroke="#E5F2F1" />
            <XAxis dataKey="branch_name" tick={{ fontSize: 11, fill: '#4A6B68' }} /><YAxis tick={{ fontSize: 11, fill: '#4A6B68' }} /><Tooltip /><Legend />
            <Bar dataKey="total_clients" fill="#0D8B7D" name="Clients" radius={[4, 4, 0, 0]} />
            <Bar dataKey="total_sessions" fill="#F5B041" name="Sessions" radius={[4, 4, 0, 0]} />
            <Bar dataKey="revenue" fill="#4A6B68" name="Revenue" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}

function MonthWiseReport({ data }) {
  const { rows = [], kpis = {} } = data || {};
  const chartData = rows.map(r => ({ ...r, label: fmtMonth(r.month) }));
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KpiCard label="Sessions This Month" value={kpis.sessions_this_month} icon={CalendarDays} />
        <KpiCard label="Revenue This Month" value={kpis.revenue_this_month} icon={IndianRupee} prefix="Rs." />
        <KpiCard label="New Clients This Month" value={kpis.new_clients_this_month} icon={Users} />
      </div>
      <DataTable testId="month-table" columns={[
        { key: 'month', label: 'Month', format: fmtMonth },
        { key: 'total_sessions', label: 'Total Sessions' },
        { key: 'completed', label: 'Completed' },
        { key: 'cancelled', label: 'Cancelled' },
        { key: 'new_clients', label: 'New Clients' },
        { key: 'revenue', label: 'Revenue', format: fmtINR },
      ]} rows={rows} />
      <ChartCard title="Month-over-Month Trend" testId="month-chart">
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={chartData}><CartesianGrid strokeDasharray="3 3" stroke="#E5F2F1" />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#4A6B68' }} />
            <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#4A6B68' }} />
            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#4A6B68' }} />
            <Tooltip /><Legend />
            <Bar yAxisId="left" dataKey="total_sessions" fill="#0D8B7D" name="Sessions" radius={[4, 4, 0, 0]} opacity={0.7} />
            <Line yAxisId="right" type="monotone" dataKey="revenue" stroke="#F5B041" strokeWidth={2} name="Revenue" dot={{ fill: '#F5B041' }} />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}

function NewClientsReport({ data }) {
  const { rows = [], kpis = {} } = data || {};
  const chartData = rows.map(r => ({ ...r, label: fmtMonth(r.month) }));
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="This Month" value={kpis.this_month} icon={Users} />
        <KpiCard label="This Quarter" value={kpis.this_quarter} icon={Users} />
        <KpiCard label="This Year" value={kpis.this_year} icon={Users} />
        <KpiCard label="Growth vs Last Month" value={kpis.growth_pct} icon={TrendingUp} suffix="%" trend={kpis.growth_pct} />
      </div>
      <DataTable testId="new-clients-table" columns={[
        { key: 'month', label: 'Month', format: fmtMonth },
        { key: 'adult', label: 'Adult' },
        { key: 'child', label: 'Child' },
        { key: 'relationship', label: 'Relationship' },
        { key: 'total', label: 'Total' },
      ]} rows={rows} />
      <ChartCard title="New Client Acquisition Trend" testId="new-clients-chart">
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={chartData}><CartesianGrid strokeDasharray="3 3" stroke="#E5F2F1" />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#4A6B68' }} /><YAxis tick={{ fontSize: 11, fill: '#4A6B68' }} /><Tooltip /><Legend />
            <Bar dataKey="adult" stackId="a" fill="#0D8B7D" name="Adult" />
            <Bar dataKey="child" stackId="a" fill="#F5B041" name="Child" />
            <Bar dataKey="relationship" stackId="a" fill="#4A6B68" name="Relationship" radius={[4, 4, 0, 0]} />
            <Line type="monotone" dataKey="total" stroke="#D3544E" strokeWidth={2} name="Total" dot={{ fill: '#D3544E' }} />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}

function FootfallReport({ data }) {
  const { rows = [], kpis = {} } = data || {};
  const chartData = rows.map(r => ({ ...r, label: fmtMonth(r.period) }));
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KpiCard label="Footfall This Month" value={kpis.this_month} icon={CalendarDays} />
        <KpiCard label="Footfall This Quarter" value={kpis.this_quarter} icon={CalendarDays} />
        <KpiCard label="Footfall This Year" value={kpis.this_year} icon={CalendarDays} />
      </div>
      <DataTable testId="footfall-table" columns={[
        { key: 'period', label: 'Period', format: fmtMonth },
        { key: 'in_person', label: 'In-Person' },
        { key: 'video', label: 'Video' },
        { key: 'tele', label: 'Tele' },
        { key: 'total', label: 'Total Footfall' },
      ]} rows={rows} />
      <ChartCard title="Footfall by Session Type" testId="footfall-chart">
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={chartData}><CartesianGrid strokeDasharray="3 3" stroke="#E5F2F1" />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#4A6B68' }} /><YAxis tick={{ fontSize: 11, fill: '#4A6B68' }} /><Tooltip /><Legend />
            <Bar dataKey="in_person" stackId="a" fill="#0D8B7D" name="In-Person" />
            <Bar dataKey="video" stackId="a" fill="#F5B041" name="Video" />
            <Bar dataKey="tele" stackId="a" fill="#4A6B68" name="Tele" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}

function RevenueReport({ data }) {
  const { rows = [], kpis = {} } = data || {};
  const chartData = rows.map(r => ({ ...r, label: fmtMonth(r.period) }));
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Revenue This Month" value={kpis.this_month} icon={IndianRupee} prefix="Rs." />
        <KpiCard label="Revenue This Quarter" value={kpis.this_quarter} icon={IndianRupee} prefix="Rs." />
        <KpiCard label="Revenue This Year" value={kpis.this_year} icon={IndianRupee} prefix="Rs." />
        <KpiCard label="Growth vs Last Month" value={kpis.growth_pct} icon={TrendingUp} suffix="%" trend={kpis.growth_pct} />
      </div>
      <DataTable testId="revenue-table" columns={[
        { key: 'period', label: 'Period', format: fmtMonth },
        { key: 'cash', label: 'Cash', format: fmtINR },
        { key: 'upi', label: 'UPI', format: fmtINR },
        { key: 'card', label: 'Card', format: fmtINR },
        { key: 'bank_transfer', label: 'Bank Transfer', format: fmtINR },
        { key: 'other', label: 'Other', format: fmtINR },
        { key: 'total', label: 'Total Revenue', format: fmtINR },
      ]} rows={rows} />
      <ChartCard title="Revenue Trend" testId="revenue-chart">
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={chartData}><CartesianGrid strokeDasharray="3 3" stroke="#E5F2F1" />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#4A6B68' }} /><YAxis tick={{ fontSize: 11, fill: '#4A6B68' }} /><Tooltip /><Legend />
            <Area type="monotone" dataKey="total" fill="#0D8B7D" fillOpacity={0.1} stroke="#0D8B7D" strokeWidth={2} name="Total Revenue" />
            <Bar dataKey="cash" fill="#0D8B7D" name="Cash" opacity={0.7} />
            <Bar dataKey="upi" fill="#F5B041" name="UPI" opacity={0.7} />
            <Bar dataKey="card" fill="#4A6B68" name="Card" opacity={0.7} />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}

function OverallReport({ data, isAdmin }) {
  const { kpis = {}, clients_by_type = [], sessions_by_status = [], sessions_by_type = [], client_trend = [], revenue_trend = [], branch_comparison = [], revenue_by_branch = [] } = data || {};
  const clientChart = client_trend?.map(r => ({ ...r, label: fmtMonth(r.month) })) || [];
  const revChart = revenue_trend?.map(r => ({ ...r, label: fmtMonth(r.month) })) || [];
  return (
    <div className="space-y-5">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <KpiCard label="Active Clients" value={kpis.active_clients} icon={Users} />
        <KpiCard label="Total Sessions" value={kpis.total_sessions} icon={CalendarDays} />
        <KpiCard label="Total Revenue" value={kpis.total_revenue} icon={IndianRupee} prefix="Rs." />
        <KpiCard label="Branches" value={kpis.total_branches} icon={Building2} />
        <KpiCard label="Therapists" value={kpis.total_therapists} icon={Stethoscope} />
      </div>

      {/* Section 1: Client Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Clients by Type" testId="clients-by-type-chart">
          <ResponsiveContainer width="100%" height={220}>
            <PieChart><Pie data={clients_by_type?.filter(c => c.value > 0)} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, value }) => `${name}: ${value}`}>
              {clients_by_type?.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Pie><Tooltip /></PieChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="New Clients Trend" testId="client-trend-chart">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={clientChart}><CartesianGrid strokeDasharray="3 3" stroke="#E5F2F1" />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#4A6B68' }} /><YAxis tick={{ fontSize: 10, fill: '#4A6B68' }} /><Tooltip />
              <Line type="monotone" dataKey="new_clients" stroke="#0D8B7D" strokeWidth={2} dot={{ fill: '#0D8B7D', r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Section 2: Session Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Sessions by Status" testId="sessions-by-status-chart">
          <ResponsiveContainer width="100%" height={220}>
            <PieChart><Pie data={sessions_by_status?.filter(s => s.value > 0)} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={80} label={({ name, value }) => `${name}: ${value}`}>
              {sessions_by_status?.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Pie><Tooltip /></PieChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Sessions by Type" testId="sessions-by-type-chart">
          <ResponsiveContainer width="100%" height={220}>
            <PieChart><Pie data={sessions_by_type?.filter(s => s.value > 0)} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, value }) => `${name}: ${value}`}>
              {sessions_by_type?.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Pie><Tooltip /></PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Section 3: Revenue Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Revenue Trend" testId="overall-revenue-trend-chart">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={revChart}><CartesianGrid strokeDasharray="3 3" stroke="#E5F2F1" />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#4A6B68' }} /><YAxis tick={{ fontSize: 10, fill: '#4A6B68' }} /><Tooltip />
              <Line type="monotone" dataKey="revenue" stroke="#F5B041" strokeWidth={2} dot={{ fill: '#F5B041', r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
        {isAdmin && revenue_by_branch?.length > 0 && (
          <ChartCard title="Revenue by Branch" testId="revenue-by-branch-chart">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={revenue_by_branch} layout="vertical"><CartesianGrid strokeDasharray="3 3" stroke="#E5F2F1" />
                <XAxis type="number" tick={{ fontSize: 10, fill: '#4A6B68' }} /><YAxis type="category" dataKey="branch" tick={{ fontSize: 10, fill: '#4A6B68' }} width={100} /><Tooltip />
                <Bar dataKey="revenue" fill="#0D8B7D" name="Revenue" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        )}
      </div>

      {/* Section 4: Branch Comparison */}
      {isAdmin && branch_comparison?.length > 0 && (
        <DataTable testId="branch-comparison-table" columns={[
          { key: 'branch', label: 'Branch' },
          { key: 'clients', label: 'Clients' },
          { key: 'sessions', label: 'Sessions' },
          { key: 'revenue', label: 'Revenue', format: fmtINR },
          { key: 'avg_sessions_per_client', label: 'Avg Sessions/Client' },
        ]} rows={branch_comparison} />
      )}
    </div>
  );
}
